import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useMsal } from '@azure/msal-react';
import { is } from 'bpmn-js/lib/util/ModelUtil';
import {
  isSafeVideoUrl,
  isSharePointShareUrl,
  isLegacyStreamUrl,
} from '../../utils/sanitize';
import { uploadVideo, createSharePointVideoSession, resolveApiUrl } from '../../utils/apiClient';
import { optimizeImage } from '../../utils/imageProcessing';
import VideoUploadConfirmDialog from '../VideoUploadConfirmDialog/VideoUploadConfirmDialog';
import './VideoPlayer.css';

/**
 * Floating, draggable video player panel.
 * Lets the user load a video URL, play/pause, and assign the current
 * timestamp or a screenshot of the current frame to the selected BPMN Task.
 *
 * Props:
 *   modeler   – the bpmn-js BpmnModeler instance
 *   onClose   – callback to close the panel
 *   onSeek    – ref-callback: parent can call onSeek.current(seconds) to seek
 */
const VideoPlayer = ({ modeler, onClose, onSeek, onVideoUrlChange, sessionVideoUrl, sessionVideoFileName, sessionVideoTime, sessionVideoPosition, onSessionVideoLoaded, onSessionTimeUpdate, onSessionPositionChange }) => {
  const { instance: msalInstance, accounts } = useMsal();
  const account = accounts && accounts[0];
  const videoRef = useRef(null);
  const titlebarRef = useRef(null);
  const panelRef = useRef(null);
  const fileInputRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [localFileName, setLocalFileName] = useState(sessionVideoFileName || '');
  const [loadedUrl, setLoadedUrl] = useState(sessionVideoUrl || '');
  // For SharePoint videos: keep the original sharing link so we can
  // re-resolve a fresh download URL when the short-lived one expires.
  const [originalShareUrl, setOriginalShareUrl] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const reResolveTried = useRef(false);
  const [currentTime, setCurrentTime] = useState(sessionVideoTime || 0);
  const [selectedElement, setSelectedElement] = useState(null);
  const [status, setStatus] = useState(null);
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const [useCors, setUseCors] = useState(!sessionVideoUrl?.startsWith('blob:'));
  const [screenshotsAvailable, setScreenshotsAvailable] = useState(true);
  const corsRetried = useRef(false);
  const lastReportedTime = useRef(0);
  // Pending file picked via "📁 Datei" — held until the user decides
  // whether to upload it to bpmn.weissenberg-group.de or keep local.
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  // ---- Expose seek function to parent via ref callback ----
  useEffect(() => {
    if (onSeek) {
      onSeek.current = (seconds) => {
        if (videoRef.current && loadedUrl) {
          videoRef.current.currentTime = seconds;
          videoRef.current.pause();
        }
      };
    }
  }, [onSeek, loadedUrl]);

  // ---- Read video URL from definitions on mount (only if no session URL) ----
  useEffect(() => {
    // If we already have a session video (local file or previously loaded URL), skip definitions read
    if (sessionVideoUrl) return;
    if (!modeler) return;
    try {
      const definitions = modeler.getDefinitions();
      if (!definitions) return;
      let url;
      try { url = definitions.get('custom:videoUrl'); } catch { /* moddle fallback */ }
      if (!url) url = definitions.$attrs?.['custom:videoUrl'];
      if (url && isSafeVideoUrl(url)) {
        setVideoUrl(url);
        // Defer to the unified loader so SharePoint links are resolved.
        // We can't await here, but loadVideoFromUrl handles its own state.
        loadVideoFromUrl(url, { persist: false, reportSession: false });
      }
    } catch { /* definitions not ready */ }
  }, [modeler, sessionVideoUrl]);

  // ---- Restore drag position from session on mount ----
  useEffect(() => {
    if (sessionVideoPosition && panelRef.current) {
      panelRef.current.style.left = sessionVideoPosition.x + 'px';
      panelRef.current.style.top = sessionVideoPosition.y + 'px';
      panelRef.current.style.right = 'auto';
    }
  }, []); // run once on mount

  // ---- Restore playback position when video loads ----
  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current && sessionVideoTime > 0) {
      videoRef.current.currentTime = sessionVideoTime;
    }
  }, [sessionVideoTime]);

  // ---- Listen for selection changes on the canvas ----
  useEffect(() => {
    if (!modeler) return;
    let eventBus;
    try { eventBus = modeler.get('eventBus'); } catch { return; }

    const onSelectionChanged = (e) => {
      const newSelection = e.newSelection;
      if (newSelection?.length === 1) {
        const el = newSelection[0];
        if (is(el, 'bpmn:Task')) {
          setSelectedElement(el);
          return;
        }
      }
      setSelectedElement(null);
    };

    eventBus.on('selection.changed', onSelectionChanged);
    return () => {
      try { eventBus.off('selection.changed', onSelectionChanged); } catch { /* cleanup */ }
    };
  }, [modeler]);

  // ---- Update time display while playing ----
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTimeUpdate = () => {
      const t = video.currentTime;
      setCurrentTime(t);
      // Report back to parent every ~2 seconds to avoid excessive re-renders
      if (onSessionTimeUpdate && Math.abs(t - lastReportedTime.current) >= 2) {
        lastReportedTime.current = t;
        onSessionTimeUpdate(t);
      }
    };
    video.addEventListener('timeupdate', onTimeUpdate);
    // Also report final position on pause/end
    const onPauseOrEnd = () => {
      if (onSessionTimeUpdate) {
        lastReportedTime.current = video.currentTime;
        onSessionTimeUpdate(video.currentTime);
      }
    };
    video.addEventListener('pause', onPauseOrEnd);
    video.addEventListener('ended', onPauseOrEnd);
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('pause', onPauseOrEnd);
      video.removeEventListener('ended', onPauseOrEnd);
    };
  }, [loadedUrl, onSessionTimeUpdate]);

  // ---- Persist URL to BPMN definitions ----
  const persistUrlToDefinitions = useCallback((url) => {
    if (!modeler) return;
    try {
      const definitions = modeler.getDefinitions();
      if (!definitions) return;
      definitions.set('custom:videoUrl', url);
      if (!definitions.$attrs) definitions.$attrs = {};
      definitions.$attrs['custom:videoUrl'] = url;
      // Signal unsaved change so the save button activates
      modeler.get('eventBus').fire('commandStack.changed');
    } catch (e) {
      console.warn('Could not save videoUrl to definitions:', e);
    }
  }, [modeler]);

  // ---- Unified loader: handles plain HTTPS and SharePoint sharing URLs ----
  const loadVideoFromUrl = useCallback(async (rawUrl, opts = {}) => {
    const { persist = true, reportSession = true } = opts;
    const url = (rawUrl || '').trim();
    if (!url) return;

    if (isLegacyStreamUrl(url)) {
      setStatus({ type: 'error', text: 'Microsoft Stream (Classic) wird nicht unterstützt. Bitte den SharePoint-Sharing-Link verwenden.' });
      return;
    }
    if (!isSafeVideoUrl(url)) {
      setStatus({ type: 'error', text: 'Ungültige oder unsichere Video-URL.' });
      return;
    }

    // Reset CORS state for new URL
    corsRetried.current = false;
    reResolveTried.current = false;
    setUseCors(true);
    setScreenshotsAvailable(true);
    setLocalFileName('');

    if (isSharePointShareUrl(url)) {
      setOriginalShareUrl(url);
      setIsResolving(true);
      setStatus({ type: 'info', text: '🔗 SharePoint-Video-Session wird erstellt…' });
      try {
        const { streamUrl } = await createSharePointVideoSession({
          msalInstance,
          account,
          videoUrl: url,
        });
        setLoadedUrl(resolveApiUrl(streamUrl));
        if (persist) persistUrlToDefinitions(url);
        if (onVideoUrlChange) onVideoUrlChange(true);
        if (reportSession && onSessionVideoLoaded) onSessionVideoLoaded(url, '');
        setStatus({ type: 'info', text: 'SharePoint-Video wird temporär geladen…' });
      } catch (err) {
        const msg = err && err.message ? err.message : 'SharePoint-Auflösung fehlgeschlagen';
        setStatus({ type: 'error', text: msg });
        setLoadedUrl('');
      } finally {
        setIsResolving(false);
      }
      return;
    }

    // Plain HTTPS URL — load directly.
    setOriginalShareUrl('');
    setLoadedUrl(url);
    if (persist) persistUrlToDefinitions(url);
    if (onVideoUrlChange) onVideoUrlChange(true);
    if (reportSession && onSessionVideoLoaded) onSessionVideoLoaded(url, '');
    setStatus({ type: 'info', text: 'Video wird geladen…' });
  }, [msalInstance, account, persistUrlToDefinitions, onVideoUrlChange, onSessionVideoLoaded]);

  // ---- Load video URL (button handler) ----
  const handleLoadUrl = useCallback(() => {
    loadVideoFromUrl(videoUrl);
  }, [loadVideoFromUrl, videoUrl]);

  // ---- Load local file ----
  const handleLocalFile = useCallback((e) => {
    const file = e.target.files[0];
    // Reset input so the same file can be re-selected
    e.target.value = '';
    if (!file) return;
    // Defer the actual load until the user confirms in the upload dialog.
    setPendingFile(file);
  }, []);

  // ---- Use the picked file as a local blob (no upload) ----
  const useLocalBlob = useCallback((file) => {
    // Previous blob URLs are managed by App.js session state — no revoke here
    const blobUrl = URL.createObjectURL(file);
    setUseCors(false); // blob URLs don't need CORS
    setScreenshotsAvailable(true); // blob URLs always allow screenshots
    corsRetried.current = false;
    setOriginalShareUrl('');
    setLoadedUrl(blobUrl);
    setVideoUrl('');
    setLocalFileName(file.name);
    setStatus({ type: 'info', text: `Lokale Datei „${file.name}" wird geladen…` });
    if (onSessionVideoLoaded) onSessionVideoLoaded(blobUrl, file.name);
  }, [onSessionVideoLoaded]);

  // ---- Upload the picked file to bpmn.weissenberg-group.de ----
  const uploadAndLoad = useCallback(async (file) => {
    setUploadProgress({ loaded: 0, total: file.size });
    setStatus({ type: 'info', text: `„${file.name}" wird hochgeladen…` });
    try {
      const { url } = await uploadVideo(file, {
        msalInstance,
        account,
        onProgress: (loaded, total) => setUploadProgress({ loaded, total }),
      });
      // Reset CORS state — uploaded videos are same-origin so this is safe.
      setUseCors(true);
      setScreenshotsAvailable(true);
      corsRetried.current = false;
      setOriginalShareUrl('');
      setLoadedUrl(url);
      setVideoUrl(url);
      setLocalFileName('');
      // Persist the public URL so it survives diagram reloads.
      persistUrlToDefinitions(url);
      if (onVideoUrlChange) onVideoUrlChange(true);
      if (onSessionVideoLoaded) onSessionVideoLoaded(url, '');
      setStatus({ type: 'info', text: 'Hochgeladenes Video wird geladen…' });
    } catch (err) {
      const msg = err && err.message ? err.message : 'Upload fehlgeschlagen';
      setStatus({ type: 'error', text: msg });
    } finally {
      setUploadProgress(null);
    }
  }, [msalInstance, account, persistUrlToDefinitions, onVideoUrlChange, onSessionVideoLoaded]);

  // ---- Dialog handlers ----
  const handleUploadConfirm = useCallback(() => {
    const file = pendingFile;
    setPendingFile(null);
    if (file) uploadAndLoad(file);
  }, [pendingFile, uploadAndLoad]);

  const handleUseLocal = useCallback(() => {
    const file = pendingFile;
    setPendingFile(null);
    if (file) useLocalBlob(file);
  }, [pendingFile, useLocalBlob]);

  const handleCancelPending = useCallback(() => {
    setPendingFile(null);
  }, []);

  // ---- Format seconds → MM:SS.ss ----
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 100);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(ms).padStart(2, '0')}`;
  };

  // ---- Assign timestamp to selected task ----
  const handleAssignTimestamp = useCallback(() => {
    if (!selectedElement || !modeler) return;
    try {
      const modeling = modeler.get('modeling');
      const ts = currentTime.toFixed(2);
      modeling.updateModdleProperties(selectedElement, selectedElement.businessObject, {
        'custom:videoTimestamp': ts
      });
      const name = selectedElement.businessObject.name || selectedElement.id;
      setStatus({ type: 'success', text: `⏱ Timestamp ${formatTime(currentTime)} → "${name}" zugewiesen` });
    } catch (e) {
      console.error('Error assigning timestamp:', e);
      setStatus({ type: 'error', text: 'Fehler beim Zuweisen des Timestamps' });
    }
  }, [selectedElement, modeler, currentTime]);

  // ---- Screenshot current frame → assign as imageUrl ----
  const [isCapturing, setIsCapturing] = useState(false);
  const handleAssignScreenshot = useCallback(async () => {
    if (!selectedElement || !modeler || !videoRef.current) return;
    setIsCapturing(true);
    try {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const rawDataUrl = canvas.toDataURL('image/png');
      // Resize & compress (max 1920×1080, JPEG 0.82 quality)
      const dataUrl = await optimizeImage(rawDataUrl);

      const modeling = modeler.get('modeling');
      // Read timestamp directly from video element to avoid stale state
      const videoTime = video.currentTime;
      const ts = videoTime.toFixed(2);
      modeling.updateModdleProperties(selectedElement, selectedElement.businessObject, {
        'custom:imageUrl': dataUrl,
        'custom:videoTimestamp': ts
      });
      const name = selectedElement.businessObject.name || selectedElement.id;
      setStatus({ type: 'success', text: `📸 Screenshot + ⏱ ${formatTime(videoTime)} → "${name}" zugewiesen` });
    } catch (e) {
      console.error('Error capturing screenshot:', e);
      setStatus({ type: 'error', text: 'Screenshot fehlgeschlagen. Ist das Video CORS-kompatibel?' });
    } finally {
      setIsCapturing(false);
    }
  }, [selectedElement, modeler]);

  // ---- Drag logic (attached via ref to avoid S6847 on non-interactive div) ----
  const onMouseDown = useCallback((e) => {
    if (!panelRef.current) return;
    const rect = panelRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setDragging(true);
  }, []);

  useEffect(() => {
    const el = titlebarRef.current;
    if (!el) return;
    el.addEventListener('mousedown', onMouseDown);
    return () => el.removeEventListener('mousedown', onMouseDown);
  }, [onMouseDown]);

  useEffect(() => {
    if (!dragging) return;
    const onMouseMove = (e) => {
      if (!panelRef.current) return;
      const newX = e.clientX - dragOffset.current.x;
      const newY = e.clientY - dragOffset.current.y;
      panelRef.current.style.left = newX + 'px';
      panelRef.current.style.top = newY + 'px';
      panelRef.current.style.right = 'auto';
    };
    const onMouseUp = () => {
      setDragging(false);
      // Report final position to session state
      if (onSessionPositionChange && panelRef.current) {
        const rect = panelRef.current.getBoundingClientRect();
        onSessionPositionChange({ x: rect.left, y: rect.top });
      }
    };
    globalThis.addEventListener('mousemove', onMouseMove);
    globalThis.addEventListener('mouseup', onMouseUp);
    return () => {
      globalThis.removeEventListener('mousemove', onMouseMove);
      globalThis.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragging]);

  // ---- Video events ----
  const onVideoLoaded = () => {
    setStatus({ type: 'success', text: screenshotsAvailable ? 'Video geladen ✓' : 'Video geladen ✓ (Screenshots nicht verfügbar – CORS fehlt)' });
    // Allow re-resolving again once the next expiry happens
    reResolveTried.current = false;
    // Restore playback position from session
    handleLoadedMetadata();
  };
  const onVideoError = () => {
    // SharePoint download URLs expire after ~1 h. If we still have the
    // original sharing link, try re-resolving once before giving up.
    if (originalShareUrl && !reResolveTried.current) {
      reResolveTried.current = true;
      setStatus({ type: 'info', text: '🔗 SharePoint-Session wird erneuert…' });
      setIsResolving(true);
      createSharePointVideoSession({
        msalInstance,
        account,
        videoUrl: originalShareUrl,
      })
        .then(({ streamUrl }) => {
          setLoadedUrl(resolveApiUrl(streamUrl));
          setStatus({ type: 'info', text: 'SharePoint-Video wird neu geladen…' });
        })
        .catch((err) => {
          const msg = err && err.message ? err.message : 'SharePoint-Session konnte nicht erneuert werden';
          setStatus({ type: 'error', text: msg });
        })
        .finally(() => setIsResolving(false));
      return;
    }
    // If CORS was enabled and this is a remote URL, retry without CORS
    if (useCors && !corsRetried.current && loadedUrl && !loadedUrl.startsWith('blob:')) {
      corsRetried.current = true;
      setUseCors(false);
      setScreenshotsAvailable(false);
      setStatus({ type: 'info', text: 'CORS nicht unterstützt – lade Video ohne Screenshot-Unterstützung…' });
      // Force video reload by briefly clearing URL
      const url = loadedUrl;
      setLoadedUrl('');
      setTimeout(() => setLoadedUrl(url), 50);
      return;
    }
    setStatus({ type: 'error', text: 'Video konnte nicht geladen werden – URL prüfen' });
  };

  // ---- Key handler: Space = play/pause (attached via ref to avoid S6847) ----
  const handleKeyDown = useCallback((e) => {
    if (e.code === 'Space' && videoRef.current) {
      e.preventDefault();
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, []);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const selectedName = selectedElement
    ? (selectedElement.businessObject.name || selectedElement.id)
    : null;

  // S3358 / S7735: extracted nested ternary for screenshot button title
  let screenshotButtonTitle = '';
  if (!screenshotsAvailable) {
    screenshotButtonTitle = 'Screenshots nicht verfügbar (CORS fehlt auf dem Video-Server)';
  } else if (!selectedElement) {
    screenshotButtonTitle = 'Bitte zuerst einen Task im Diagramm auswählen';
  }

  return (
    <section
      className="video-player-overlay"
      ref={panelRef}
      tabIndex={-1}
      aria-label="Video Player"
    >
      {/* Titlebar (drag handle) */}
      <div
        className="video-player-titlebar"
        ref={titlebarRef}
      >
        <span className="video-player-title">🎬 Video Player</span>
        <button className="video-player-close" onClick={onClose} title="Schließen">✕</button>
      </div>

      <div className="video-player-body">
        {/* URL input + local file picker */}
        <div className="video-url-row">
          <input
            className="video-url-input"
            type="text"
            placeholder="Video-URL oder SharePoint-Link eingeben"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !isResolving) handleLoadUrl(); }}
          />
          <button className="video-url-btn" onClick={handleLoadUrl} disabled={isResolving}>
            {isResolving ? '…' : 'Laden'}
          </button>
          <button
            className="video-url-btn video-file-btn"
            onClick={() => fileInputRef.current?.click()}
            title="Lokale Videodatei auswählen"
          >
            📁 Datei
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            style={{ display: 'none' }}
            onChange={handleLocalFile}
          />
        </div>
        {localFileName && (
          <div className="video-local-file-info">
            📄 {localFileName}
          </div>
        )}

        {/* Video or placeholder */}
        {loadedUrl ? (
          <>
            <video
              ref={videoRef}
              className="video-player-video"
              src={loadedUrl}
              controls
              crossOrigin={useCors ? 'anonymous' : undefined}
              onLoadedData={onVideoLoaded}
              onError={onVideoError}
            >
              <track kind="captions" />
            </video>

            {/* Timestamp display */}
            <div className="video-timestamp-display">
              <span className="video-timestamp-time">{formatTime(currentTime)}</span>
              <span className="video-selected-element">
                {selectedName
                  ? <>Ausgewählt: <strong>{selectedName}</strong></>
                  : 'Kein Task ausgewählt'}
              </span>
            </div>

            {/* Action buttons */}
            <div className="video-actions">
              <button
                className="video-action-btn assign-timestamp"
                disabled={!selectedElement}
                onClick={handleAssignTimestamp}
                title={selectedElement ? '' : 'Bitte zuerst einen Task im Diagramm auswählen'}
              >
                ⏱ Timestamp zuweisen
              </button>
              <button
                className="video-action-btn assign-screenshot"
                disabled={!selectedElement || !screenshotsAvailable || isCapturing}
                onClick={handleAssignScreenshot}
                title={screenshotButtonTitle}
              >
                {isCapturing
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Wird optimiert…</>
                  : '📸 Screenshot zuweisen'}
              </button>
            </div>
          </>
        ) : (
          <div className="video-placeholder">
            <div className="video-placeholder-icon">🎬</div>
            <div className="video-placeholder-text">
              Video-URL oben eingeben und <strong>Laden</strong> klicken,<br />
              um den Prozess-Video-Player zu starten.
            </div>
          </div>
        )}

        {/* Status message */}
        {status && (
          <div className={`video-status ${status.type}`}>{status.text}</div>
        )}
        {uploadProgress && uploadProgress.total > 0 && (
          <div className="video-upload-progress">
            <div
              className="video-upload-progress-bar"
              style={{ width: `${Math.round((uploadProgress.loaded / uploadProgress.total) * 100)}%` }}
            />
            <span className="video-upload-progress-label">
              {Math.round((uploadProgress.loaded / uploadProgress.total) * 100)}%
              {' · '}
              {(uploadProgress.loaded / (1024 * 1024)).toFixed(1)} / {(uploadProgress.total / (1024 * 1024)).toFixed(1)} MB
            </span>
          </div>
        )}
      </div>

      <VideoUploadConfirmDialog
        isOpen={!!pendingFile}
        fileName={pendingFile?.name}
        fileSize={pendingFile?.size}
        onUpload={handleUploadConfirm}
        onUseLocal={handleUseLocal}
        onCancel={handleCancelPending}
      />
    </section>
  );
};

VideoPlayer.propTypes = {
  modeler: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSeek: PropTypes.shape({ current: PropTypes.func }),
  onVideoUrlChange: PropTypes.func,
  sessionVideoUrl: PropTypes.string,
  sessionVideoFileName: PropTypes.string,
  sessionVideoTime: PropTypes.number,
  sessionVideoPosition: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  onSessionVideoLoaded: PropTypes.func,
  onSessionTimeUpdate: PropTypes.func,
  onSessionPositionChange: PropTypes.func,
};

export default VideoPlayer;
